import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import StatusBadge, { PriorityBadge, AcknowledgmentBadge } from '../components/ui/StatusBadge';
import TabsComponent from '../components/ui/TabsComponent';
import { db, COLLECTIONS } from '../firebase/config';
import { deleteDoc, doc } from 'firebase/firestore';
import { 
  CheckSquare,
  Clock,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Calendar,
  User,
  MessageSquare,
  Paperclip,
  Edit,
  Trash2,
  ChevronLeft,
  Plus,
  FileText,
  Activity,
  Info,
  Search,
  BarChart3,
  FolderOpen,
  Building2,
  Globe,
  X,
  Users,
  List
} from 'lucide-react';

const Tasks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, hasPermission } = useAuth();
  const { 
    tasks, 
    users, 
    projects,
    departments,
    teams,
    updateTask, 
    acknowledgeTask,
    createTask,
    createNotification 
  } = useData();
  
  const [selectedTask, setSelectedTask] = useState(null);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState({
    title: '',
    description: '',
    assigneeId: '',
    assigneeIds: [],
    teamMembers: [],
    priority: 'important_not_urgent',
    dueDate: '',
    estimate: 0,
    status: 'ממתין לעבודה',
    projectId: ''
  });
  const [editAssigneeDeptFilter, setEditAssigneeDeptFilter] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const [comment, setComment] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'details';
  });
  const [mainTab, setMainTab] = useState(
    currentUser?.role === 'team_manager' ? 'team' : 
    currentUser?.role === 'employee' ? 'personal' : 'management'
  ); // New state for main tabs
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCompleted, setFilterCompleted] = useState(false);
const [filterAssignee, setFilterAssignee] = useState('all');
const [filterDepartment, setFilterDepartment] = useState('all');
const [filterProject, setFilterProject] = useState('all');
const [filterTeam, setFilterTeam] = useState('all');


  // Helper function to check if task should be hidden from current user
  const shouldHideTask = (task) => {
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    
    // הסתרת משימות אישיות של עובדים אחרים
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser.id && task.createdBy !== currentUser.id;
    
    // הסתרת משימות של פרויקטים אישיים של עובדים אחרים
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser.id && taskProject.createdBy !== currentUser.id;
    
    return hidePersonalTask || hidePersonalProjectTask;
  };
  
  // פונקציה חדשה ליצירת התראות למנהלים על משימות אישיות

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['details', 'comments', 'activity', 'files'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);
  // Get task if ID is provided
  useEffect(() => {
    if (id) {
      const task = tasks.find(t => t.id === id);
      setSelectedTask(task || null);
    } else {
      setSelectedTask(null);
    }
  }, [id, tasks]);

  // Ensure default assignee is the current user doc from users collection (by email)
  useEffect(() => {
    if (!showNewTask && selectedTask) return; // don't override when viewing/editing existing
    if (!currentUser?.email || !Array.isArray(users) || users.length === 0) return;
    const me = users.find(u => u.email === currentUser.email);
    if (me && newTask.assigneeId !== me.id) {
      setNewTask(prev => ({ 
        ...prev, 
        assigneeId: me.id, 
        assigneeIds: prev.assigneeIds?.length ? prev.assigneeIds : [me.id],
        teamMembers: prev.teamMembers?.length ? prev.teamMembers : [me.id]
      }));
      setAssigneeDeptFilter('');
      setEditAssigneeDeptFilter('');
      setNewTask({
        title: '',
        description: '',
        projectId: '',
        assigneeId: '',
        assigneeIds: [],
        teamMembers: [],
        departmentId: currentUser?.departmentId || '',
        teamId: '',
        scope: 'personal',
        dueDate: '',
        estimate: 0,
        priority: 'important_not_urgent'
      });
    }
  }, [users, currentUser?.email, showNewTask, selectedTask]);

  // New task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    projectId: '',
    assigneeId: '',
    assigneeIds: [],
    teamMembers: [],
    departmentId: currentUser?.departmentId || '',
    teamId: '',
    scope: 'personal',
    dueDate: '',
    dueTime: '09:00', // שעת יעד ברירת מחדל
    estimate: 0,
    priority: 'important_not_urgent',
    subtasks: ''
  });
  // State לחיפוש עובדים בטופס החדש
const [assigneeDeptFilter, setAssigneeDeptFilter] = useState('');
  const [newSubtask, setNewSubtask] = useState('');

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    await updateTask(selectedTask.id, { status: newStatus });
  };

  // Confirm status change with note
  const confirmStatusChange = async () => {
    if (!statusNote.trim()) {
      alert('נא להזין הערה');
      return;
    }

    await updateTask(selectedTask.id, { 
      status: pendingStatus,
      statusNote: statusNote,
      statusUpdatedAt: new Date()
    });

    setShowStatusModal(false);
    setStatusNote('');
    setPendingStatus('');
  };

  // Handle acknowledgment
  const handleAcknowledge = async () => {
    await acknowledgeTask(selectedTask.id);
  };
// פונקציה חדשה ליצירת התראות למנהלים על משימות אישיות
const notifyManagersAboutPersonalTask = async (taskData, taskId) => {
  // מוצא את העובד שיצר את המשימה
  const taskUser = users.find(u => u.id === taskData.assigneeId);
  if (!taskUser) return;
  
  // קובע למי לשלוח התראות לפי התפקיד
  const managersToNotify = users.filter(user => {
    if (user.id === taskUser.id) return false; // לא לעצמו
    
    switch(taskUser.role) {
      case 'employee':
        return user.teamId === taskUser.teamId && user.role === 'team_manager' ||
               user.departmentId === taskUser.departmentId && user.role === 'dept_manager' ||
               ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
               
      case 'team_manager':
        return user.departmentId === taskUser.departmentId && user.role === 'dept_manager' ||
               ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
               
      case 'dept_manager':
        return ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
        
      case 'deputy_ceo':
        return ['ceo', 'org_admin'].includes(user.role);
        
      default:
        return false;
    }
  });
  
  // יוצר התראות
  for (const manager of managersToNotify) {
    await createNotification({
      userId: manager.id,
      type: 'personal_task_created',
      title: 'משימה אישית חדשה',
      message: `${taskUser.name} קבע/ה משימה אישית: ${taskData.title}`,
      taskId: taskId,
      createdAt: new Date(),
      isRead: false,
      link: '/team-availability'
    });
  }
};
  // Handle create task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // Add team members to assigneeIds if not already included
    // מצא את המשתמש הנוכחי ברשימת המשתמשים
const currentUserDoc = users.find(u => u.email === currentUser.email);
const assigneeUserDoc = users.find(u => u.id === newTask.assigneeId);

const taskData = {
  ...newTask,
  createdBy: currentUserDoc?.id || currentUser.id,
  creatorId: currentUserDoc?.id || currentUser.id,
  assigneeId: assigneeUserDoc?.id || newTask.assigneeId,
  assigneeEmail: assigneeUserDoc?.email || currentUser.email,
  scope: currentUser?.role === 'employee' ? 'personal' : newTask.scope,
  assigneeIds: [...new Set([...newTask.assigneeIds, ...newTask.teamMembers])],
  isNew: true,          // ← הוסף רק את זה
  acknowledged: false   // ← ואת זה
};
// הוסף את זה אחרי שורה 207 ב-Tasks.jsx
console.log('=== יצירת משימה חדשה ===');
console.log('currentUser (מ-Auth):', currentUser);
console.log('currentUserDoc (מ-users):', currentUserDoc);
console.log('assigneeUserDoc:', assigneeUserDoc);
console.log('taskData:', taskData);
console.log('האם זו משימה אישית?', taskData.scope === 'personal');

// וודא שלמשימות אישיות יש את כל השדות הנדרשים
if (taskData.scope === 'personal') {
  console.log('=== בדיקת שדות למשימה אישית ===');
  console.log('assigneeId:', taskData.assigneeId);
  console.log('assigneeEmail:', taskData.assigneeEmail);
  console.log('dueDate:', taskData.dueDate);
  console.log('dueTime:', taskData.dueTime);
}
console.log('=== יצירת משימה חדשה ===');
console.log('סוג משימה:', taskData.scope);
console.log('מוקצה ל:', taskData.assigneeId);
console.log('תאריך:', taskData.dueDate);
console.log('שעה:', taskData.dueTime);
// הוסף שדות חדשים ל-taskData


// יצירת המשימה
const taskId = await createTask(taskData);

// אם זו משימה אישית - יצירת התראות למנהלים
if (taskData.type === 'personal' || taskData.scope === 'personal') {
  await notifyManagersAboutPersonalTask(taskData, taskId);
}

// יצירת התראות למוקצים - הוסף את זה!
for (const assigneeId of taskData.assigneeIds) {
  if (assigneeId !== currentUser.id) {
    const assignee = users.find(u => u.id === assigneeId);
    await createNotification({
      userId: assigneeId,
      type: 'task_assigned',
      title: 'משימה חדשה הוקצתה לך',
      message: `המשימה "${taskData.title}" הוקצתה לך`,
      taskId: taskId,
      createdAt: new Date(),
      isRead: false,
      isNew: true,
      link: `/tasks/${taskId}`
    });
  }
}

// התראות למחלקה/צוות אם רלוונטי - הוסף את זה!
if (taskData.departmentId && taskData.scope === 'managerial') {
  const deptUsers = users.filter(u => u.departmentId === taskData.departmentId);
  for (const user of deptUsers) {
    if (!taskData.assigneeIds.includes(user.id)) {
      await createNotification({
        userId: user.id,
        type: 'department_task',
        title: 'משימה חדשה במחלקה',
        message: `משימה חדשה "${taskData.title}" נוספה למחלקה שלך`,
        taskId: taskId,
        createdAt: new Date(),
        isRead: false,
        isNew: true,
        link: `/tasks/${taskId}`
      });
    }
  }
}

if (taskData.teamId) {
  const teamUsers = users.filter(u => u.teamId === taskData.teamId);
  for (const user of teamUsers) {
    if (!taskData.assigneeIds.includes(user.id)) {
      await createNotification({
        userId: user.id,
        type: 'team_task',
        title: 'משימה חדשה בצוות',
        message: `משימה חדשה "${taskData.title}" נוספה לצוות שלך`,
        taskId: taskId,
        createdAt: new Date(),
        isRead: false,
        isNew: true,
        link: `/tasks/${taskId}`
      });
    }
  }
} 
      setShowNewTask(false);
      setNewTask({
        title: '',
        description: '',
        projectId: '',
        assigneeId: '',
        assigneeIds: [],
        teamMembers: [],
        departmentId: currentUser?.departmentId || '',
        teamId: '',
        scope: 'personal',
        dueDate: '',
        estimate: 0,
        priority: 'important_not_urgent',
        subtasks: ''
      });
      setAssigneeDeptFilter('');
      setEditAssigneeDeptFilter('');
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  // Handle delete task
  const handleDeleteTask = async (task) => {
    try {
      // Delete task from Firestore
      await deleteDoc(doc(db, COLLECTIONS.TASKS, task.id));
      console.log('Task deleted successfully');
      // The task will be automatically removed from the list due to real-time updates
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('שגיאה במחיקת המשימה');
    }
  };

  // Can edit task
  const canEditTask = (task) => {
    if (!task || !currentUser) return false;
    
    // Creator can always edit
    if (task.createdBy === currentUser.id || task.creatorId === currentUser.id) return true;
    
    // Assignee can edit their own tasks
    if (task.assigneeId === currentUser.id && task.scope === 'personal') return true;
    
    // Managers can edit department tasks
    if (currentUser.role === 'dept_manager' && task.departmentId === currentUser.departmentId) return true;
    
    // Org admin can edit all
    if (currentUser.role === 'org_admin') return true;
    
    return false;
  };

  // Tasks list view
  if (!selectedTask) {
    // Filter tasks based on user role
    
    const filteredTasks = tasks.filter(task => {
      // סנן משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
      if (task.isDailyRoutine || task.routineId) {
        return false;
      }
      
       if (mainTab === 'personal' && task.scope === 'personal') {
    console.log('=== בדיקת משימה אישית ===');
    console.log('שם משימה:', task.title);
    console.log('task.createdBy:', task.createdBy);
    console.log('task.creatorId:', task.creatorId);
    console.log('task.assigneeId:', task.assigneeId);
    console.log('currentUser.id:', currentUser.id);
    console.log('currentUser.email:', currentUser.email);
    
    const meUser = users.find(u => u.email === currentUser.email);
    console.log('meUser:', meUser);
    console.log('meUser?.id:', meUser?.id);
    console.log('---');
  }
      // First filter by user role and access
      let hasAccess = false;
      
      if (currentUser?.role === 'employee') {
        // Employee - depends on selected tab
        if (mainTab === 'management') {
  // Management tab - see organizational tasks from their team/department
  hasAccess = false;
  
  // בדוק אם המשימה מוקצית לצוות שלי
  if (task.teamId) {
    const myTeams = teams.filter(t => {
      const user = users.find(u => u.id === currentUser.id);
      if (user?.teamIds && Array.isArray(user.teamIds)) {
        return user.teamIds.includes(t.id);
      }
      if (user?.teamId) {
        return user.teamId === t.id;
      }
      return false;
    });
    hasAccess = myTeams.some(team => team.id === task.teamId);
  }
  
  // או אם המשימה מוקצית למחלקה שלי
  if (!hasAccess && task.departmentId && currentUser.departmentId) {
    hasAccess = task.departmentId === currentUser.departmentId;
  }
  
  // ווודא שזו משימה ארגונית
  hasAccess = hasAccess && task.scope === 'managerial';
} else {
          // Personal tab - see only their personal tasks
          const meUser = users.find(u => u.email === currentUser.email);
const myIds = [currentUser.id, meUser?.id].filter(Boolean);

hasAccess = (task.scope === 'personal' && 
            (task.assigneeId === currentUser.id || 
             task.assigneeId === meUser?.id ||
             task.createdBy === currentUser.id ||
             task.createdBy === meUser?.id ||
             task.creatorId === currentUser.id ||
             task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'dept_manager') {
        // Department manager - depends on selected tab
        if (mainTab === 'management') {
          // מנהל מחלקה רואה גם משימות של צוותים במחלקה שלו
          hasAccess = task.departmentId === currentUser.departmentId ||
                     (task.teamId && teams.some(team => team.id === task.teamId && team.departmentId === currentUser.departmentId));
        } else {
          // Personal tab - see only their personal tasks
        const meUser = users.find(u => u.email === currentUser.email);
const myIds = [currentUser.id, meUser?.id].filter(Boolean);

hasAccess = (task.scope === 'personal' && 
            (task.assigneeId === currentUser.id || 
             task.assigneeId === meUser?.id ||
             task.createdBy === currentUser.id ||
             task.createdBy === meUser?.id ||
             task.creatorId === currentUser.id ||
             task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'team_manager') {
        // Team manager - depends on selected tab
        if (mainTab === 'team') {
          // Team tab - see tasks assigned to their team members (including managerial tasks)
          const userTeam = teams.find(team => team.managerId === currentUser.id);
          hasAccess = userTeam && (
            task.teamId === userTeam.id ||
            (task.scope === 'managerial' && task.teamId === userTeam.id)
          );
        } else {
          // Personal tab - see only their personal tasks
         const meUser = users.find(u => u.email === currentUser.email);
const myIds = [currentUser.id, meUser?.id].filter(Boolean);

hasAccess = (task.scope === 'personal' && 
            (task.assigneeId === currentUser.id || 
             task.assigneeId === meUser?.id ||
             task.createdBy === currentUser.id ||
             task.createdBy === meUser?.id ||
             task.creatorId === currentUser.id ||
             task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') {
        // מנהל ראשי וסמנכ"ל - תלוי בטאב שנבחר
        if (mainTab === 'management') {
          // מנהלים עליונים רואים כל המשימות הארגוניות כולל משימות צוותיות
          hasAccess = (task.scope === 'managerial' || task.scope === 'department' || 
                      (task.scope === 'managerial' && task.teamId));
        } else {
          // טאב אישי - רק משימות אישיות שלו
         const meUser = users.find(u => u.email === currentUser.email);
         hasAccess = (task.scope === 'personal' && 
            (task.createdBy === currentUser.id || 
             task.createdBy === meUser?.id ||
             task.assigneeId === currentUser.id ||
             task.assigneeId === meUser?.id ||
             task.creatorId === currentUser.id ||
             task.creatorId === meUser?.id));
        }
      }
      
      if (!hasAccess) return false;
      
      // Check if task should be hidden
      if (shouldHideTask(task)) return false;
      
      // Then apply search and filter criteria
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = filterStatus === 'all' || 
                     task.status === filterStatus ||
                     (filterStatus === 'במסלול' && task.status === 'בתהליך') ||
                     (filterStatus === 'בתהליך' && task.status === 'במסלול');
      
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
      
      // Fix the completed filter logic
      let matchesCompleted = true;
      if (filterCompleted) {
        // If filterCompleted is true, show only completed tasks
        matchesCompleted = task.status === 'הושלם';
      } else {
        // If filterCompleted is false, show all tasks (including completed ones)
        matchesCompleted = true;
      }
      

      
      // פילטרים חדשים
const matchesAssignee = filterAssignee === 'all' || task.assigneeId === filterAssignee;
const matchesDepartment = filterDepartment === 'all' || task.departmentId === filterDepartment;
const matchesProject = filterProject === 'all' || task.projectId === filterProject;
const matchesTeam = filterTeam === 'all' || task.teamId === filterTeam;

return matchesSearch && matchesStatus && matchesPriority && matchesCompleted && 
       matchesAssignee && matchesDepartment && matchesProject && matchesTeam;
    });
    


    return (
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
            <h1 className="text-xl md:text-2xl font-bold">משימות</h1>
            <button
              onClick={() => setShowNewTask(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 min-h-[44px] touch-manipulation"
            >
              <Plus size={20} />
              משימה חדשה
            </button>
          </div>
        </div>

        {/* Main Tabs for Managers and Admins */}
        {(currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b">
              <nav className="flex flex-wrap gap-2 sm:space-x-8 px-4 sm:px-6">
                <button
                  onClick={() => setMainTab('management')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'management'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 size={20} />
                    ארגוני
                  </div>
                </button>
                <button
                  onClick={() => setMainTab('personal')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'personal'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User size={20} />
                    אישי
                  </div>
                </button>
              </nav>
            </div>
            <div className="p-4">
              <div className="text-sm text-gray-600">
                {mainTab === 'management' ? (
                  <span>צפייה בכל המשימות הארגוניות של החברה</span>
                ) : (
                  <span>צפייה במשימות האישיים שלך</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Team Manager Tabs */}
        {currentUser?.role === 'team_manager' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setMainTab('team')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'team'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users size={20} />
                    צוותי
                  </div>
                </button>
                <button
                  onClick={() => setMainTab('personal')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'personal'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User size={20} />
                    אישי
                  </div>
                </button>
              </nav>
            </div>
            <div className="p-4">
              <div className="text-sm text-gray-600">
                {mainTab === 'team' ? (
                  <span>צפייה במשימות של הצוות שלך</span>
                ) : (
                  <span>צפייה במשימות האישיים שלך</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Department Manager and Employee Tabs */}
        {(currentUser?.role === 'dept_manager' || currentUser?.role === 'employee') && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setMainTab('management')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'management'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 size={20} />
                    ארגוני
                  </div>
                </button>
                <button
                  onClick={() => setMainTab('personal')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'personal'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User size={20} />
                    אישי
                  </div>
                </button>
              </nav>
            </div>
            <div className="p-4">
              <div className="text-sm text-gray-600">
                {mainTab === 'management' ? (
                  currentUser?.role === 'dept_manager' ? (
                    <span>צפייה במשימות של המחלקה שלך</span>
                  ) : (
                    <span>צפייה במשימות הארגוניות שלך</span>
                  )
                ) : (
                  <span>צפייה במשימות האישיים שלך</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
<div className="bg-white rounded-lg shadow p-4 mb-6">
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
    {/* Search */}
    <div className="sm:col-span-2 lg:col-span-3 xl:col-span-2">
      <div className="relative">
        <Search className="absolute right-3 top-3 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="חיפוש משימות..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pr-10 px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
        />
      </div>
    </div>

    {/* Status Filter - מעודכן */}
    <select 
      value={filterStatus}
      onChange={(e) => setFilterStatus(e.target.value)}
      className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
    >
      <option value="all">כל הסטטוסים</option>
      <option value="ממתין לאישור">ממתין לאישור</option>
      <option value="ממתין לעבודה">ממתין לעבודה</option>
     <option value="במסלול">במסלול</option>
      <option value="הושלם">הושלם</option>
    </select>

    {/* Priority Filter */}
    <select 
      value={filterPriority}
      onChange={(e) => setFilterPriority(e.target.value)}
      className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
    >
      <option value="all">כל העדיפויות</option>
      <option value="important_urgent">חשוב ודחוף</option>
      <option value="important_not_urgent">חשוב ולא דחוף</option>
      <option value="not_important_urgent">דחוף ולא חשוב</option>
      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
    </select>

    {/* Assignee Filter - חדש */}
    <select 
      value={filterAssignee}
      onChange={(e) => setFilterAssignee(e.target.value)}
      className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
    >
      <option value="all">כל המוקצים</option>
      {users.map(user => (
        <option key={user.id} value={user.id}>{user.name}</option>
      ))}
    </select>

    {/* Department Filter - חדש */}
    <select 
      value={filterDepartment}
      onChange={(e) => setFilterDepartment(e.target.value)}
      className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
    >
      <option value="all">כל המחלקות</option>
      {departments.map(dept => (
        <option key={dept.id} value={dept.id}>{dept.name}</option>
      ))}
    </select>

    {/* Project Filter - חדש */}
    <select 
      value={filterProject}
      onChange={(e) => setFilterProject(e.target.value)}
      className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
    >
      <option value="all">כל הפרויקטים</option>
      {projects.map(project => (
        <option key={project.id} value={project.id}>{project.name}</option>
      ))}
    </select>

    {/* Team Filter - חדש למנכל וסמנכל */}
    {(currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') && (
      <select 
        value={filterTeam}
        onChange={(e) => setFilterTeam(e.target.value)}
        className="px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full"
      >
        <option value="all">כל הצוותים</option>
        {teams.map(team => (
          <option key={team.id} value={team.id}>{team.name}</option>
        ))}
      </select>
    )}

    {/* Show Completed Tasks Separately */}
    <button 
      onClick={() => setFilterCompleted(!filterCompleted)}
      className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] w-full sm:w-auto touch-manipulation ${
        filterCompleted 
          ? 'bg-green-600 text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {filterCompleted ? 'הצג הכל' : 'משימות שהושלמו'}
    </button>
  </div>
</div>

        {/* Tasks Grid */}
        {filterCompleted && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-green-700 mb-2">משימות שהושלמו</h2>
            <p className="text-sm text-gray-600">משימות שהושלמו בהצלחה</p>
          </div>
        )}
        
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
  {filteredTasks.map(task => {
    const assignee = users.find(u => u.id === task.assigneeId);
    const project = task && projects.find(p => p.id === task.projectId)
    const creatorId = task.creatorId || task.createdBy;
    const creator = users.find(u => u.id === creatorId);
   const taskDepartment = task && departments.find(d => d.id === task.departmentId);
   const taskTeam = task && teams.find(t => t.id === task.teamId);
    return (
      <div
  key={task.id}
  className={`group relative bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer p-6 ${
    task.status === 'הושלם' ? 'border-2 border-green-200' : 'border border-gray-200'
  } ${task.isNew && !task.acknowledged ? 'ring-2 ring-purple-500 ring-opacity-50' : ''}`}
  onClick={() => navigate(`/tasks/${task.id}`)}
>
  {/* New Task Indicator - הוסף את זה מיד אחרי פתיחת ה-div */}
  {task.isNew && !task.acknowledged && (
    <div className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
      חדש!
    </div>
  )}

   {/* 👇 הוסף את הכפתור כאן - אחרי התג "חדש!" */}
{task.isNew && !task.acknowledged && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      updateTask(task.id, { 
        isNew: false,
        acknowledged: true 
      });
    }}
    className="absolute top-10 left-4 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 transition-all opacity-0 group-hover:opacity-100"
  >
    סמן כנקרא
  </button>
)}
  
  {/* Status Badge */}
  <div className="absolute top-4 right-4 z-10">
    <StatusBadge status={task.status || 'ממתין לעבודה'} size="sm" type="task" />
  </div>
        {/* Status Badge */}
        <div className="absolute top-4 right-4 z-10">
          <StatusBadge status={task.status || 'ממתין לעבודה'} size="sm" type="task" />
        </div>

        {/* Action buttons */}
        <div className="absolute top-4 left-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTask(task);
              const initialAssignees = Array.isArray(task.assigneeIds) && task.assigneeIds.length
                ? task.assigneeIds
                : (task.assigneeId ? [task.assigneeId] : []);
              setEditTaskForm({
                title: task.title || '',
                description: task.description || '',
                assigneeId: initialAssignees[0] || currentUser.id,
                assigneeIds: initialAssignees,
                teamMembers: task.teamMembers || [],
                priority: task.priority || 'important_not_urgent',
                dueDate: task.dueDate || '',
                dueTime: task.dueTime || '09:00',
                estimate: task.estimate || 0,
                status: task.status || 'ממתין לעבודה',
                projectId: task.projectId || ''
              });
              setShowEditTask(true);
            }}
            className="p-1 text-gray-600 hover:text-blue-600"
            title="ערוך משימה"
          >
            <Edit size={16} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTask(task);
              setShowStatusModal(true);
            }}
            className="p-1 text-gray-600 hover:text-blue-600"
            title="שנה סטטוס"
          >
            <BarChart3 size={16} />
          </button>
          {canEditTask(task) && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`האם אתה בטוח שברצונך למחוק את המשימה "${task.title}"?`)) {
                  handleDeleteTask(task);
                }
              }}
              className="p-1 text-gray-600 hover:text-red-600"
              title="מחק משימה"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>

        {/* Header */}
        <div className="mb-4 mt-8">
          <h3 className="font-bold text-lg text-gray-800 mb-1">
            {task.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">
            {task.description}
          </p>
        </div>

        {/* Department, Team and Project Badges */}
        <div className="mb-3 space-y-2">
          {taskDepartment && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mr-2">
              <Building2 size={12} />
              {taskDepartment.name}
            </span>
          )}
          {taskTeam && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full mr-2">
              <Users size={12} />
              {taskTeam.name}
            </span>
          )}
          {project && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              <FolderOpen size={12} />
              {project.name}
            </span>
          )}
        </div>

        {/* Meta info */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <CheckSquare size={14} />
            <span>{task.subtasks?.length || 0} תתי משימות</span>
          </div>
          
          {assignee && (
            <div className="flex items-center gap-2 text-gray-600">
              <User size={14} />
              <span>מוקצה ל: {assignee.name}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-gray-600">
            <User size={14} />
            <span>נוצר ע"י {creator?.name || 'לא ידוע'}</span>
          </div>
          
          {/* Priority */}
          <div className="flex items-center gap-2 text-gray-600">
            <AlertTriangle size={14} />
            <span>
              {(() => {
                const priorityLabels = {
                  'important_urgent': 'חשוב ודחוף',
                  'important_not_urgent': 'חשוב ולא דחוף',
                  'not_important_urgent': 'דחוף ולא חשוב',
                  'not_important_not_urgent': 'לא חשוב ולא דחוף'
                };
                return priorityLabels[task.priority] || 'לא מוגדרת';
              })()}
            </span>
          </div>

          {/* Due Date */}
          {task.dueDate && (
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar size={14} />
              <span>יעד: {new Date(task.dueDate).toLocaleDateString('he-IL')}{task.dueTime && ` ${task.dueTime}`}</span>
            </div>
          )}
        </div>
      </div>
    );
  })}
</div> 

        {/* New Task Modal */}
        {showNewTask && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewTask(false)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <Plus size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">משימה חדשה</h2>
                      <p className="text-purple-100 text-sm">צור משימה חדשה במערכת</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNewTask(false)}
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-30 transition-all"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                <form onSubmit={handleCreateTask}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <FileText size={16} className="text-purple-600" />
                          כותרת המשימה
                        </label>
                        <input
                          type="text"
                          value={newTask.title}
                          onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          placeholder="הכנס כותרת למשימה..."
                          required
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <FileText size={16} className="text-purple-600" />
                          תיאור המשימה
                        </label>
                        <textarea
                          value={newTask.description}
                          onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all h-24 resize-none"
                          placeholder="תאר את המשימה בפירוט..."
                          rows="3"
                        />
                      </div>

                      {/* Scope */}
                      {/* Scope field - only show for non-employees */}
                      {currentUser?.role !== 'employee' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <Globe size={16} className="text-purple-600" />
                            תחום
                          </label>
                          <select
                            value={newTask.scope}
                            onChange={(e) => setNewTask({...newTask, scope: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          >
                            <option value="personal">אישי</option>
                            <option value="managerial">ניהולי</option>
                          </select>
                        </div>
                      )}

                      {/* Priority - Only for Personal Scope or for employees (who always have personal scope) */}
                      {(newTask.scope === 'personal' || currentUser?.role === 'employee') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <AlertTriangle size={16} className="text-purple-600" />
                            עדיפות
                          </label>
                          <select
                            value={newTask.priority}
                            onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          >
                            <option value="important_urgent">🔥 חשוב ודחוף</option>
                            <option value="important_not_urgent">⭐ חשוב ולא דחוף</option>
                            <option value="not_important_urgent">⚡ לא חשוב ודחוף</option>
                            <option value="not_important_not_urgent">📝 לא חשוב ולא דחוף</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
     {/* Assignee Selection - Show only for managerial scope */}
{newTask.scope === 'managerial' && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
      <User size={16} className="text-purple-600" />
      הקצה משימה ל
    </label>
    <div className="space-y-2">
      {/* Search input */}
      <input
        type="text"
        placeholder="חפש עובד..."
        value={assigneeDeptFilter}
        onChange={(e) => setAssigneeDeptFilter(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
      />
      {/* Employee list with checkboxes */}
      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
        {users
          .filter(user => 
            user.name.toLowerCase().includes(assigneeDeptFilter.toLowerCase()) ||
            user.email.toLowerCase().includes(assigneeDeptFilter.toLowerCase())
          )
          .map(user => (
            <label key={user.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={newTask.assigneeIds.includes(user.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setNewTask({
                      ...newTask,
                      assigneeIds: [...newTask.assigneeIds, user.id],
                      assigneeId: newTask.assigneeIds.length === 0 ? user.id : newTask.assigneeId
                    });
                  } else {
                    const updatedIds = newTask.assigneeIds.filter(id => id !== user.id);
                    setNewTask({
                      ...newTask,
                      assigneeIds: updatedIds,
                      assigneeId: updatedIds.length > 0 ? updatedIds[0] : ''
                    });
                  }
                }}
                className="text-purple-600 rounded"
              />
              <span className="text-sm">{user.name} - {user.email}</span>
            </label>
          ))}
      </div>
    </div>
  </div>
)}
                      {/* Dynamic Fields Based on Scope */}
                      {(newTask.scope === 'managerial' && currentUser?.role !== 'employee') ? (
                        <>
                          {/* Department Assignment */}
                          {currentUser?.role !== 'employee' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Building2 size={16} className="text-purple-600" />
                                הקצאה למחלקה
                              </label>
                              <select
                                value={newTask.departmentId}
                                onChange={(e) => setNewTask({ ...newTask, departmentId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                              >
                                <option value="">בחר מחלקה</option>
                                {departments && departments.map(dept => (
                                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Team Assignment */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <Users size={16} className="text-purple-600" />
                              הקצאה לצוות
                            </label>
                            <select
                              value={newTask.teamId || ''}
                              onChange={(e) => setNewTask({ ...newTask, teamId: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            >
                              <option value="">בחר צוות</option>
                              {teams && teams
                                .filter(team => !newTask.departmentId || team.departmentId === newTask.departmentId)
                                .filter(team => team.isActive)
                                .map(team => (
                                  <option key={team.id} value={team.id}>
                                    {team.name}
                                    {team.departmentId && ` - ${departments.find(d => d.id === team.departmentId)?.name || 'לא ידוע'}`}
                                  </option>
                                ))}
                            </select>
                          </div>

                          {/* Project */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <FolderOpen size={16} className="text-purple-600" />
                              פרויקט
                            </label>
                            <select
                              value={newTask.projectId}
                              onChange={(e) => setNewTask({...newTask, projectId: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            >
                              <option value="">בחר פרויקט</option>
                              {(currentUser?.role === 'employee' 
                                ? projects.filter(p => 
                                    (p.scope === 'personal' && p.assigneeId === currentUser.id) || 
                                    p.assigneeId === currentUser.id || 
                                    (Array.isArray(p.teamMembers) && p.teamMembers.includes(currentUser.id))
                                  )
                                : projects
                              ).map(project => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                  {project.departmentId && ` - ${departments.find(d => d.id === project.departmentId)?.name || 'לא ידוע'}`}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Priority for Managerial */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <AlertTriangle size={16} className="text-purple-600" />
                              עדיפות
                            </label>
                            <select
                              value={newTask.priority}
                              onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            >
                              <option value="important_urgent">🔥 חשוב ודחוף</option>
                              <option value="important_not_urgent">⭐ חשוב ולא דחוף</option>
                              <option value="not_important_urgent">⚡ לא חשוב ודחוף</option>
                              <option value="not_important_not_urgent">📝 לא חשוב ולא דחוף</option>
                            </select>
                          </div>
                        </>
                      ) : (
                        /* Personal Scope - Simple Fields */
                        <>
                          {/* Project for Personal */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <FolderOpen size={16} className="text-purple-600" />
                              פרויקט
                            </label>
                            <select
                              value={newTask.projectId}
                              onChange={(e) => setNewTask({...newTask, projectId: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            >
                              <option value="">ללא פרויקט</option>
                              {(currentUser?.role === 'employee' 
                                ? projects.filter(p => 
                                    (p.scope === 'personal' && p.assigneeId === currentUser.id) || 
                                    p.assigneeId === currentUser.id || 
                                    (Array.isArray(p.teamMembers) && p.teamMembers.includes(currentUser.id))
                                  )
                                : projects
                              ).map(project => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                  {project.departmentId && ` - ${departments.find(d => d.id === project.departmentId)?.name || 'לא ידוע'}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      {/* Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Calendar size={16} className="text-purple-600" />
                          תאריך יעד
                        </label>
                        <input
                          type="date"
                          value={newTask.dueDate}
                          onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                      </div>

                      {/* Time */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Clock size={16} className="text-purple-600" />
                          שעת יעד
                        </label>
                        <input
                          type="time"
                          value={newTask.dueTime}
                          onChange={(e) => setNewTask({...newTask, dueTime: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                      </div>

                      {/* Estimate */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Clock size={16} className="text-purple-600" />
                          הערכת זמן
                        </label>
                        <input
                          type="number"
                          value={newTask.estimate}
                          onChange={(e) => setNewTask({...newTask, estimate: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          min="0"
                          placeholder="שעות"
                        />
                      </div>
                    </div>

                    {/* Subtasks - Full Width */}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <List size={16} className="text-purple-600" />
                        תתי משימות
                      </label>
                      <div className="space-y-2">
                        {newTask.subtasks && newTask.subtasks.length > 0 && (
                          <div className="space-y-2">
                            {newTask.subtasks.map((subtask, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                                  {typeof subtask === 'string' ? subtask : subtask.text}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedSubtasks = newTask.subtasks.filter((_, i) => i !== index);
                                    setNewTask({...newTask, subtasks: updatedSubtasks});
                                  }}
                                  className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-all"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newSubtask || ''}
                            onChange={(e) => setNewSubtask(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && newSubtask.trim()) {
                                e.preventDefault();
                                const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                                setNewTask({...newTask, subtasks: updatedSubtasks});
                                setNewSubtask('');
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            placeholder="הוסף תת משימה חדשה..."
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newSubtask && newSubtask.trim()) {
                                const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                                setNewTask({...newTask, subtasks: updatedSubtasks});
                                setNewSubtask('');
                              }
                            }}
                            className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg flex items-center gap-2 transition-all font-medium"
                          >
                            <Plus size={16} />
                            הוסף
                          </button>
                        </div>
                      </div>
                    </div>


                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-8 flex gap-3 justify-end pt-6 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setShowNewTask(false)}
                      className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all font-medium"
                    >
                      ביטול
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <div className="flex items-center gap-2">
                        <Plus size={20} />
                        צור משימה
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Task detail view
  const assignee = users.find(u => u.id === selectedTask.assigneeId);
  const assignees = Array.isArray(selectedTask.assigneeIds) && selectedTask.assigneeIds.length
    ? selectedTask.assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean)
    : (assignee ? [assignee] : []);
  const creatorId = selectedTask.creatorId || selectedTask.createdBy;
  const creator = users.find(u => u.id === creatorId);
  const project = projects.find(p => p.id === selectedTask.projectId);
  const taskTeam = teams.find(t => t.id === selectedTask.teamId);
const taskDepartment = departments.find(d => d.id === selectedTask.departmentId); 
  // Tabs for task details
  const taskTabs = [
    {
      id: 'details',
      label: 'פרטים',
      icon: Info,
      content: <TaskDetails task={selectedTask} assignee={assignee} creator={creator} project={project} team={taskTeam} department={taskDepartment} />
      
    },
    {
      id: 'comments',
      label: 'הערות',
      icon: MessageSquare,
      content: <TaskComments task={selectedTask} />
    },
    {
      id: 'activity',
      label: 'פעילות',
      icon: Activity,
      content: <TaskActivity task={selectedTask} />
    },
    {
      id: 'files',
      label: 'קבצים',
      icon: Paperclip,
      content: <TaskFiles task={selectedTask} />
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => {
            navigate('/tasks');
            setSelectedTask(null);
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ChevronLeft size={20} />
          חזרה למשימות
        </button>
        
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{selectedTask.title}</h1>
                              <StatusBadge status={selectedTask.status || 'במסלול'} type="task" />
              <PriorityBadge priority={selectedTask.priority || 'important_not_urgent'} />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>נוצר על ידי {creator?.name}</span>
              <span>•</span>
              <span>{new Date(selectedTask.createdAt?.toDate ? selectedTask.createdAt.toDate() : selectedTask.createdAt).toLocaleDateString('he-IL')}</span>
            </div>
          </div>
          
          {canEditTask(selectedTask) && (
            <div className="flex gap-2">
              <button
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  const initialAssignees = Array.isArray(selectedTask.assigneeIds) && selectedTask.assigneeIds.length
                    ? selectedTask.assigneeIds
                    : (selectedTask.assigneeId ? [selectedTask.assigneeId] : []);
                            setEditTaskForm({
            title: selectedTask.title || '',
            description: selectedTask.description || '',
            assigneeId: initialAssignees[0] || currentUser.id,
            assigneeIds: initialAssignees,
            teamMembers: selectedTask.teamMembers || [],
            priority: selectedTask.priority || 'important_not_urgent',
            dueDate: selectedTask.dueDate || '',
            estimate: selectedTask.estimate || 0,
            status: selectedTask.status || 'במסלול',
            projectId: selectedTask.projectId || ''
          });
                  setShowEditTask(true);
                }}
                title="ערוך משימה"
              >
                <Edit size={20} />
              </button>
              
              <button
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                onClick={() => {
                  setShowStatusModal(true);
                }}
                title="שנה סטטוס"
              >
                <BarChart3 size={20} />
              </button>
              
              <button 
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                onClick={() => {
                  if (window.confirm(`האם אתה בטוח שברצונך למחוק את המשימה "${selectedTask.title}"?`)) {
                    handleDeleteTask(selectedTask);
                    navigate('/tasks');
                  }
                }}
                title="מחק משימה"
              >
                <Trash2 size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Acknowledgment Alert */}
      {!selectedTask.acknowledged && selectedTask.assigneeId === currentUser.id && (creatorId !== currentUser.id) && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-yellow-600" size={20} />
              <span className="font-medium">משימה זו ממתינה לאישור קבלה</span>
            </div>
            <button
              onClick={handleAcknowledge}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              קיבלתי/ראיתי
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <TabsComponent 
        tabs={taskTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Status Change Modal */}
      {showStatusModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">שנה סטטוס משימה</h2>
            <div className="mb-6">
              <p className="text-gray-600 mb-2">משימה: <strong>{selectedTask.title}</strong></p>
              <p className="text-sm text-gray-500">בחר סטטוס חדש למשימה</p>
            </div>
            
            <div className="space-y-3 mb-6">
              {['ממתין לעבודה', 'ממתין לאישור', 'במסלול', 'הושלם'].map(status => (
                <button
                  key={status}
                  onClick={async () => {
                    console.log('Status button clicked:', status, 'for task:', selectedTask.title);
                    try {
                      await updateTask(selectedTask.id, { status: status });
                      setShowStatusModal(false);
                    } catch (error) {
                      console.error('Error updating task status:', error);
                      alert('שגיאה בעדכון סטטוס המשימה');
                    }
                  }}
                  className={`w-full p-3 text-right rounded-lg border transition-all ${
                    selectedTask.status === status
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTask && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">ערוך משימה</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTask(selectedTask.id, editTaskForm);
                setShowEditTask(false);
              } catch (error) {
                console.error('Error updating task:', error);
                alert('שגיאה בעדכון המשימה');
              }
            }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">כותרת</label>
                  <input
                    type="text"
                    value={editTaskForm.title}
                    onChange={(e) => setEditTaskForm({...editTaskForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס</label>
                  <select
                    value={editTaskForm.status}
                    onChange={(e) => setEditTaskForm({...editTaskForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ממתין לעבודה">ממתין לעבודה</option>
                    <option value="ממתין לאישור">ממתין לאישור</option>
                   <option value="במסלול">במסלול</option>
                    <option value="הושלם">הושלם</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">עדיפות</label>
                  <select
                    value={editTaskForm.priority}
                    onChange={(e) => setEditTaskForm({...editTaskForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="important_urgent">חשוב ודחוף</option>
                    <option value="important_not_urgent">חשוב ולא דחוף</option>
                    <option value="not_important_urgent">לא חשוב ודחוף</option>
                    <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תאריך יעד</label>
                  <input
                    type="date"
                    value={editTaskForm.dueDate}
                    onChange={(e) => setEditTaskForm({...editTaskForm, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שעת יעד</label>
                  <input
                    type="time"
                    value={editTaskForm.dueTime}
                    onChange={(e) => setEditTaskForm({...editTaskForm, dueTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                <textarea
                  value={editTaskForm.description}
                  onChange={(e) => setEditTaskForm({...editTaskForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
                  rows="3"
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowEditTask(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Details Component
const TaskDetails = ({ task, assignee, creator, project, team, department }) => {
  const [completedSubtasks, setCompletedSubtasks] = useState([]);
  const { updateTask } = useData();
  
  // פונקציה לטיפול בסימון תת משימה
const handleSubtaskToggle = async (subtaskIndex) => {
  const newCompletedSubtasks = completedSubtasks.includes(subtaskIndex)
    ? completedSubtasks.filter(i => i !== subtaskIndex)
    : [...completedSubtasks, subtaskIndex];
  
  setCompletedSubtasks(newCompletedSubtasks);
  

};

// useEffect לסנכרון תתי משימות מושלמות כשהמשימה הושלמה
useEffect(() => {
  if (task.status === 'הושלם' && task.subtasks) {
    setCompletedSubtasks(task.subtasks.map((_, index) => index));
  }
}, [task.status, task.subtasks]);
  return (
    <div className="space-y-6">
      {/* תתי משימות - בראש הרשימה */}
      {task.subtasks && task.subtasks.length > 0 && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <List size={20} className="text-purple-600" />
            תתי משימות ({completedSubtasks.length}/{task.subtasks.length})
          </h3>
          <div className="space-y-3">
            {task.subtasks.map((subtask, index) => {
              const isCompleted = completedSubtasks.includes(index) || task.status === 'הושלם';
              return (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    isCompleted 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => handleSubtaskToggle(index)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      isCompleted
                        ? 'bg-green-600 border-green-600'
                        : 'bg-white border-gray-300 hover:border-green-500'
                    }`}
                    disabled={task.status === 'הושלם'}
                  >
                    {isCompleted && (
                      <CheckCircle size={12} className="text-white" />
                    )}
                  </button>
                  <span className={`flex-1 ${isCompleted ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                   {typeof subtask === 'string' ? subtask : subtask.text}
                  </span>
                  {isCompleted && (
                    <span className="text-xs text-green-600 font-medium">הושלם</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">פרטי המשימה</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">כותרת</label>
            <p className="text-gray-900">{task.title}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">סטטוס</label>
            <p className="text-gray-900">{task.status}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">עדיפות</label>
            <p className="text-gray-900">
              {(() => {
                const priorityLabels = {
                  'important_urgent': 'חשוב ודחוף',
                  'important_not_urgent': 'חשוב ולא דחוף',
                  'not_important_urgent': 'דחוף ולא חשוב',
                  'not_important_not_urgent': 'לא חשוב ולא דחוף'
                };
                return priorityLabels[task.priority] || 'לא מוגדרת';
              })()}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">תאריך ושעת יעד</label>
            <p className="text-gray-900">
              {task.dueDate ? (
                `${new Date(task.dueDate).toLocaleDateString('he-IL')}${task.dueTime ? ` ${task.dueTime}` : ''}`
              ) : 'לא נקבע'}
            </p>
          </div>
        </div>
        {task.description && (
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-600">תיאור</label>
            <p className="text-gray-900 mt-1">{task.description}</p>
          </div>
        )}
      </div>

      {assignee && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">משויך ל</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium">{assignee.name}</p>
              <p className="text-sm text-gray-600">{assignee.email}</p>
            </div>
          </div>
        </div>
      )}

      {creator && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">נוצר על ידי</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <User size={20} className="text-green-600" />
            </div>
            <div>
              <p className="font-medium">{creator.name}</p>
              <p className="text-sm text-gray-600">{creator.email}</p>
            </div>
          </div>
        </div>
      )}

      {department && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">מחלקה</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium">{department.name}</p>
              {department.description && (
                <p className="text-sm text-gray-600">{department.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {project && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">פרויקט</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FolderOpen size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="font-medium">{project.name}</p>
              <p className="text-sm text-gray-600">{project.description}</p>
            </div>
          </div>
        </div>
      )}

      {team && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">צוות</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Users size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="font-medium">{team.name}</p>
              <p className="text-sm text-gray-600">{team.description}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Task Comments Component
const TaskComments = ({ task }) => {
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const { currentUser } = useAuth();
 const { updateTask, createComment } = useData();

  // טען הערות קיימות
  useEffect(() => {
    if (task.comments) {
      setComments(task.comments);
    }
  }, [task.comments]);

  // הוסף הערה חדשה
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const newCommentObj = {
      id: Date.now().toString(),
      text: newComment,
      authorId: currentUser.id,
      authorName: currentUser.name,
      createdAt: new Date().toISOString()
    };

    const updatedComments = [...comments, newCommentObj];
    
    // עדכן ב-Firebase
    await updateTask(task.id, { comments: updatedComments });// צור הערה במערכת ההערות הכללית
await createComment({
  taskId: task.id,
  content: newComment,
  userId: currentUser.id,
  userName: currentUser.name,
  isRead: false,
  createdAt: new Date()
});
    
    setComments(updatedComments);
    setNewComment('');
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">הערות</h3>
      
      {/* טופס הוספת הערה */}
      <div className="mb-6">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="הוסף הערה..."
          rows="3"
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
        >
          הוסף הערה
        </button>
      </div>
      
      {/* רשימת הערות */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-500">אין הערות עדיין</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="border-b pb-4">
              <p className="text-gray-800">{comment.text}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm font-medium text-gray-600">
                  {comment.authorName}
                </span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-500">
                  {new Date(comment.createdAt).toLocaleDateString('he-IL')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Task Activity Component
const TaskActivity = ({ task }) => {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">פעילות</h3>
      <p className="text-gray-600">אין פעילות עדיין</p>
    </div>
  );
};

// Task Files Component
const TaskFiles = ({ task }) => {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">קבצים</h3>
      <p className="text-gray-600">אין קבצים עדיין</p>
    </div>
  );
};

export default Tasks;